import { useEffect, useRef, useState } from "react";

interface RollingNumberProps {
  value: number;
}

/**
 * A count that slides its new value in on change — up when it grows, down when
 * it shrinks — so a subtask being ticked visibly nudges the "N/M" rollup.
 *
 * Mount-safe: the digit is remounted (keyed) only when the value actually
 * changes, so the keyframe plays on a real change and never on first paint.
 * `prefers-reduced-motion` disables the slide (index.css). Purely visual — the
 * rendered text is always the current value, so it stays correct with motion
 * off or mid-animation.
 */
export function RollingNumber({ value }: RollingNumberProps) {
  const prev = useRef(value);
  const [anim, setAnim] = useState<{ dir: "up" | "down"; key: number } | null>(null);

  useEffect(() => {
    if (prev.current !== value) {
      setAnim((a) => ({ dir: value > prev.current ? "up" : "down", key: (a?.key ?? 0) + 1 }));
      prev.current = value;
    }
  }, [value]);

  return (
    <span
      style={{
        display: "inline-block",
        overflow: "hidden",
        verticalAlign: "bottom",
        lineHeight: 1.2,
      }}
    >
      <span
        key={anim?.key ?? "init"}
        className={anim ? `flow-roll-${anim.dir}` : undefined}
        style={{ display: "inline-block" }}
      >
        {value}
      </span>
    </span>
  );
}
